import { centsToDollars } from "@shared/pricing/formulas";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const currencyFormatterWhole = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyFormatterSubCent = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

export function formatCents(cents: number): string {
  return currencyFormatter.format(centsToDollars(cents));
}

export function formatCentsWhole(cents: number): string {
  return currencyFormatterWhole.format(centsToDollars(cents));
}

export function formatDollars(dollars: number): string {
  return formatCentsWhole(Math.round(dollars * 100));
}

export function formatCentsWithCurrency(
  cents: number,
  currency: string = "USD",
  maximumFractionDigits?: number,
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    ...(maximumFractionDigits === undefined ? {} : { maximumFractionDigits }),
  }).format(maximumFractionDigits !== undefined && maximumFractionDigits > 2 ? cents / 100 : centsToDollars(cents));
}

export function parseDollars(value: string): number {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const dollars = parseFloat(cleaned);
  return Math.round(dollars * 100);
}

export function formatPrice(
  cents: number | undefined | null,
  fallback: string = "—",
): string {
  return cents !== undefined && cents !== null ? formatCents(cents) : fallback;
}

export function formatCentsCompact(cents: number): string {
  return formatDollarsCompact(centsToDollars(cents));
}

export function formatDollarsCompact(dollars: number): string {
  const magnitude = Math.abs(dollars);
  const sign = dollars < 0 && Math.round(magnitude) > 0 ? "-" : "";
  if (magnitude >= 1_000_000) return `${sign}$${(magnitude / 1_000_000).toFixed(1)}M`;
  if (magnitude >= 1_000) return `${sign}$${(magnitude / 1_000).toFixed(1)}k`;
  return `${sign}$${magnitude.toFixed(0)}`;
}

export function formatCentsSmart(cents: number): string {
  if (cents % 100 === 0) return currencyFormatterWhole.format(centsToDollars(cents));
  return currencyFormatter.format(centsToDollars(cents));
}

export function formatCentsRaw(cents: number): string {
  return centsToDollars(cents).toFixed(2);
}

export function formatDollarsExact(dollars: number): string {
  return currencyFormatter.format(dollars);
}

export function formatDollarsSubCent(dollars: number): string {
  return currencyFormatterSubCent.format(dollars);
}

export function formatCentsSigned(cents: number): string {
  if (cents === 0) return formatCentsCompact(0);
  return `${cents < 0 ? "−" : "+"}${formatCentsCompact(Math.abs(cents))}`;
}
